import { nextCheckboxTick } from './tickOverrides';

/**
 * Two gestures share pointer-down on the box:
 *   - stay inside STILL → hold-for-partial ("-")
 *   - travel past PAINT → drag-to-paint
 * Anything in between cancels the hold but does not start a paint
 * (release is a normal click).
 */
export const CHECKBOX_STILL_PX = 5;
export const CHECKBOX_PAINT_THRESHOLD_PX = 14;

const painters = new Map();

let session = null;
let pendingPaintClick = false;

const onWinMove = (e) => {
  moveCheckboxPaint(e);
};

const onWinUp = (e) => {
  endCheckboxPaint(e.pointerId);
};

const bindWindow = () => {
  window.addEventListener('pointermove', onWinMove);
  window.addEventListener('pointerup', onWinUp);
  window.addEventListener('pointercancel', onWinUp);
};

const unbindWindow = () => {
  window.removeEventListener('pointermove', onWinMove);
  window.removeEventListener('pointerup', onWinUp);
  window.removeEventListener('pointercancel', onWinUp);
};

const rowToggleId = (row) =>
  row.querySelector(':scope > .checkbox-toggle')?.getAttribute('data-checkbox-id') ||
  null;

const checkboxRows = (editorRoot) => {
  if (!editorRoot) return [];
  return [...editorRoot.querySelectorAll('.checkbox-block')].filter(
    (row) => row.closest('.doc-editor') === editorRoot
  );
};

export const collectCheckboxIds = (editorRoot) =>
  checkboxRows(editorRoot).map(rowToggleId).filter(Boolean);

export const idsInPaintRange = (orderedIds, originId, hitId) => {
  const a = orderedIds.indexOf(originId);
  const b = orderedIds.indexOf(hitId);
  if (a === -1 || b === -1) return [];
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return orderedIds.slice(lo, hi + 1);
};

const checkboxIdFromNode = (node, editorRoot) => {
  const el = node?.closest?.('.checkbox-toggle, .checkbox-block');
  if (!el || !editorRoot?.contains(el)) return null;
  if (el.closest('.doc-editor') !== editorRoot) return null;
  if (el.classList.contains('checkbox-toggle')) {
    return el.getAttribute('data-checkbox-id');
  }
  return rowToggleId(el);
};

/** Checkbox under the pointer — the box itself, its row, or the row at this Y. */
export const hitCheckboxId = (clientX, clientY, editorRoot) => {
  if (!editorRoot) return null;
  const fromPoint =
    typeof document !== 'undefined'
      ? document.elementFromPoint?.(clientX, clientY)
      : null;
  const viaPoint = checkboxIdFromNode(fromPoint, editorRoot);
  if (viaPoint) return viaPoint;

  let bestId = null;
  let bestDx = Infinity;
  for (const row of checkboxRows(editorRoot)) {
    const r = row.getBoundingClientRect();
    if (clientY < r.top || clientY > r.bottom) continue;
    const midX = (r.left + r.right) / 2;
    const dx = Math.abs(clientX - midX);
    if (dx < bestDx) {
      bestDx = dx;
      bestId = rowToggleId(row);
    }
  }
  return bestId;
};

export const registerCheckboxPainter = (id, api) => {
  painters.set(id, api);
  return () => {
    if (painters.get(id) === api) painters.delete(id);
  };
};

const setPaintClass = (on) => {
  session?.editorRoot?.classList.toggle('doc-editor--checkbox-paint', on);
};

const syncPaintRange = (hitId) => {
  if (!session?.active) return;
  const inRange = new Set(
    idsInPaintRange(session.orderedIds, session.originId, hitId)
  );
  for (const id of session.orderedIds) {
    const painter = painters.get(id);
    if (!painter) continue;
    if (inRange.has(id)) {
      if (!session.originals.has(id)) session.originals.set(id, painter.getTick());
      if (painter.getTick() !== session.targetTick) {
        painter.applyTick(session.targetTick, { fromPaint: true });
      }
    } else if (session.originals.has(id)) {
      const original = session.originals.get(id);
      if (painter.getTick() !== original) {
        painter.applyTick(original, { fromPaint: true });
      }
    }
  }
};

const activatePaint = () => {
  const origin = painters.get(session.originId);
  if (!origin) return false;
  if (!session.leftStill) {
    session.leftStill = true;
    session.onLeaveStill?.();
  }
  session.active = true;
  session.orderedIds = collectCheckboxIds(session.editorRoot);
  // Paint from the tick at pointer-down, not a hold preview ("-") that
  // may have appeared if they paused before dragging.
  const fromTick = session.originTick ?? origin.getTick();
  session.targetTick = nextCheckboxTick(fromTick, 'click');
  session.originals.set(session.originId, fromTick);
  setPaintClass(true);
  session.onActivate?.();
  syncPaintRange(session.originId);
  return true;
};

export const beginCheckboxPaint = ({
  originId,
  pointerId,
  clientX,
  clientY,
  editorRoot,
  onActivate,
  onLeaveStill,
}) => {
  pendingPaintClick = false;
  if (session) endCheckboxPaint(session.pointerId);
  session = {
    originId,
    pointerId,
    startX: clientX,
    startY: clientY,
    editorRoot,
    onActivate,
    onLeaveStill,
    originTick: painters.get(originId)?.getTick(),
    active: false,
    leftStill: false,
    historyTaken: false,
    targetTick: null,
    orderedIds: [],
    originals: new Map(),
    lastHitId: originId,
  };
  bindWindow();
};

/** First paint write takes a history slot; later writes in the gesture do not. */
export const takeCheckboxPaintHistorySlot = () => {
  if (!session) return true;
  if (session.historyTaken) return false;
  session.historyTaken = true;
  return true;
};

export const moveCheckboxPaint = (e) => {
  if (!session || session.pointerId !== e.pointerId) return false;
  if (!session.active) {
    const dx = e.clientX - session.startX;
    const dy = e.clientY - session.startY;
    const dist2 = dx * dx + dy * dy;
    if (dist2 >= CHECKBOX_STILL_PX ** 2 && !session.leftStill) {
      session.leftStill = true;
      session.onLeaveStill?.();
    }
    if (dist2 < CHECKBOX_PAINT_THRESHOLD_PX ** 2) return false;
    if (!activatePaint()) return false;
  }
  const hit =
    hitCheckboxId(e.clientX, e.clientY, session.editorRoot) ||
    session.lastHitId ||
    session.originId;
  session.lastHitId = hit;
  syncPaintRange(hit);
  return true;
};

export const endCheckboxPaint = (pointerId) => {
  if (!session) return false;
  if (pointerId != null && session.pointerId !== pointerId) return false;
  const active = session.active;
  setPaintClass(false);
  session = null;
  unbindWindow();
  if (active) pendingPaintClick = true;
  return active;
};

export const consumeCheckboxPaintClick = () => {
  if (!pendingPaintClick) return false;
  pendingPaintClick = false;
  return true;
};

export const resetCheckboxPaintForTests = () => {
  if (session) {
    setPaintClass(false);
    session = null;
  }
  pendingPaintClick = false;
  unbindWindow();
};
